/**
 * Opening a mail attachment, like Gmail where the platform allows. The desktop app saves it to Downloads (a clash gets
 * "name (1).pdf") and opens it with the system's default app; the web opens PDFs, pictures and text in a new tab and
 * downloads the rest. "Download" always just saves. The bytes come with my credentials (mail is bearer-only).
 */
import { ApiFailure } from "@bundle/core";
import { fileKind, filenameWithExtension, openInTabAs } from "@bundle/core/logic/files";

export type AttachmentIntent = "open" | "download";

/** Where the desktop app saved it (for "Show in folder"); undefined on the web, where the browser has it. */
export type AttachmentOutcome = { savedPath?: string };

export type AttachmentFile = { filename: string; contentType?: string };

/** The desktop shell's downloads (desktop.ts), when the page runs inside it. */
export type DesktopDownloads = {
  /** The saved path; null when the shell predates saving (the browser's download takes over). */
  save(bytes: Uint8Array, name: string): Promise<string | null>;
  open(path: string): Promise<void>;
};

export type AttachmentEnv = {
  desktop?: DesktopDownloads;
  /** A blank tab opened during the click (popup blockers allow nothing later); null when blocked. */
  openTab(): Window | null;
  saveBlob(blob: Blob, name: string): void;
  objectUrl(blob: Blob): string;
  revokeUrl(url: string): void;
};

/** How long a tab's blob URL lives: long enough for the viewer to load and reload it. */
const TAB_URL_MS = 10 * 60_000;

/**
 * Opens or downloads `file`. Must be called from the click itself: on the web the new tab opens before anything is
 * awaited, then gets the file once it's here (or closes again if fetching fails).
 */
export async function openMailAttachment(env: AttachmentEnv, intent: AttachmentIntent, file: AttachmentFile, fetchBlob: () => Promise<Blob>): Promise<AttachmentOutcome> {
  const runs = fileKind(file.contentType ?? "", file.filename) === "executable";
  const viewAs = env.desktop === undefined && intent === "open" ? openInTabAs(file.contentType ?? "", file.filename) : undefined;
  const tab = viewAs === undefined ? null : env.openTab();
  let blob: Blob;
  try {
    blob = await fetchBlob();
  } catch (e) {
    tab?.close();
    throw e;
  }
  const name = filenameWithExtension(file.filename, file.contentType || blob.type);
  if (env.desktop !== undefined) {
    const path = await env.desktop.save(new Uint8Array(await blob.arrayBuffer()), name);
    if (path === null) {
      env.saveBlob(blob, name);
      return {};
    }
    // A program is only ever saved: opening it would run it.
    if (intent === "open" && !runs) await env.desktop.open(path);
    return { savedPath: path };
  }
  if (tab !== null && viewAs !== undefined) {
    const url = env.objectUrl(new Blob([blob], { type: viewAs }));
    tab.location.href = url;
    setTimeout(() => env.revokeUrl(url), TAB_URL_MS);
    return {};
  }
  env.saveBlob(blob, name);
  return {};
}

/** The browser's: a new tab that can't reach back to this one, titled while it loads. */
export function blankTab(title: string): Window | null {
  const tab = window.open("", "_blank");
  if (tab === null) return null;
  tab.opener = null;
  try {
    tab.document.title = title;
  } catch {
    // Not ours to title (never expected for about:blank).
  }
  return tab;
}

/** A response's body as a blob, reporting bytes as they arrive (`total` from Content-Length when it's given). */
export async function readWithProgress(res: Response, onProgress?: (loaded: number, total: number | undefined) => void): Promise<Blob> {
  const type = res.headers.get("content-type") ?? "";
  const length = Number(res.headers.get("content-length"));
  const total = Number.isFinite(length) && length > 0 ? length : undefined;
  if (res.body === null || onProgress === undefined) return res.blob();
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress(0, total);
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      onProgress(loaded, total);
    }
  } catch (e) {
    throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
  }
  return new Blob(chunks as BlobPart[], { type });
}
